import React, { useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, SafeAreaView, ActivityIndicator, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Button, DualPaneLayout, colors, spacing, typography } from '../components';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { apiService, BillingPaymentStatus } from '../services/api';
import { activateBusinessSession } from '../services/business-session';
import { openInvoice } from '../services/invoices';
import { useLocale } from '../contexts/LocaleContext';
import { useThemedStyles } from '../hooks/useThemedStyles';

const POLL_MS = 3000;
const MAX_WAIT_MS = 3 * 60 * 1000;

const readPaymentId = (routeParams: any): string | null => {
  if (routeParams?.paymentId) return String(routeParams.paymentId);
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const fromUrl = new URLSearchParams(window.location.search).get('payment');
    if (fromUrl) return fromUrl;
    try { return window.sessionStorage.getItem('dislix.pendingPaymentId'); } catch { return null; }
  }
  return null;
};

/**
 * After Kashier's hosted page. Never trusts the redirect itself: polls the
 * API (which verifies with Kashier) until the payment is final, then
 * refreshes the session so the new plan is in the token.
 */
export const BillingReturnScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, routeParams } = useAppContext();
  const { currentBusiness } = useAppStore();
  const { t, locale } = useLocale();
  const [paymentId] = useState(() => readPaymentId(routeParams));
  const [status, setStatus] = useState<BillingPaymentStatus | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const startedAt = useRef(Date.now());
  const fromOnboarding = (routeParams as any)?.from === 'onboarding';

  useEffect(() => {
    if (!paymentId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      try {
        const res = await apiService.getBillingPaymentStatus(paymentId);
        if (cancelled) return;
        if (res.statusCode === 1000 && res.data) {
          setStatus(res.data);
          if (res.data.status === 'completed') {
            try { window?.sessionStorage?.removeItem('dislix.pendingPaymentId'); } catch { /* web only */ }
            if (currentBusiness?._id) await activateBusinessSession(currentBusiness._id);
            return;
          }
          if (res.data.status !== 'pending') return;
        }
      } catch { /* keep polling */ }
      if (Date.now() - startedAt.current > MAX_WAIT_MS) {
        setTimedOut(true);
        return;
      }
      timer = setTimeout(poll, POLL_MS);
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [paymentId]);

  const state: 'checking' | 'success' | 'failed' | 'unknown' =
    !paymentId ? 'unknown'
      : status?.status === 'completed' ? 'success'
      : status && ['failed', 'expired', 'refunded'].includes(status.status) ? 'failed'
      : timedOut ? 'unknown'
      : 'checking';

  const periodEnd = status?.periodEnd
    ? new Date(status.periodEnd).toLocaleDateString(locale === 'ar' ? 'ar-EG' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

  return (
    <DualPaneLayout>
      <SafeAreaView style={styles.container}>
        <View style={styles.content}>
          {state === 'checking' && (
            <>
              <ActivityIndicator size="large" color={colors.primary as string} />
              <Text style={styles.title}>{t('billing.return.checking')}</Text>
              <Text style={styles.body}>{t('billing.return.checkingBody')}</Text>
            </>
          )}
          {state === 'success' && (
            <>
              <Ionicons name="checkmark-circle" size={64} color={colors.status.success as string} />
              <Text style={styles.title}>{t('billing.return.successTitle')}</Text>
              <Text style={styles.body}>
                {status?.kind === 'addon'
                  ? t('billing.return.addonsBody')
                  : periodEnd ? t('billing.return.successBody', { date: periodEnd }) : t('billing.return.successBodyShort')}
              </Text>
              <Button
                title={t('billing.viewInvoice')}
                onPress={() => paymentId && openInvoice(paymentId).catch(() => {})}
                variant="outline"
                size="lg"
                fullWidth
              />
              <Button
                title={fromOnboarding ? t('billing.return.continueSetup') : t('billing.return.goToBilling')}
                onPress={() => navigateTo(fromOnboarding ? 'channel-onboarding-stepper' : 'subscription')}
                variant="primary"
                size="lg"
                fullWidth
              />
            </>
          )}
          {state === 'failed' && (
            <>
              <Ionicons name="close-circle" size={64} color={colors.status.error as string} />
              <Text style={styles.title}>{t('billing.return.failedTitle')}</Text>
              <Text style={styles.body}>{status?.failureReason || t('billing.return.failedBody')}</Text>
              <Button title={t('billing.return.tryAgain')} onPress={() => navigateTo('plan-selection', undefined, fromOnboarding ? undefined : { mode: 'change' })} variant="primary" size="lg" fullWidth />
            </>
          )}
          {state === 'unknown' && (
            <>
              <Ionicons name="time-outline" size={64} color={colors.textLight as string} />
              <Text style={styles.title}>{t('billing.return.pendingTitle')}</Text>
              <Text style={styles.body}>{t('billing.return.pendingBody')}</Text>
              <Button title={t('billing.return.goToBilling')} onPress={() => navigateTo('subscription')} variant="primary" size="lg" fullWidth />
            </>
          )}
        </View>
      </SafeAreaView>
    </DualPaneLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background.surface },
  content: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: spacing.xl, gap: spacing.md, maxWidth: 460, width: '100%', alignSelf: 'center' },
  title: { fontSize: typography.fontSize.xl, fontFamily: typography.fontFamily.bold, color: colors.textDark, textAlign: 'center' },
  body: { fontSize: typography.fontSize.sm, color: colors.textLight, textAlign: 'center', marginBottom: spacing.md },
});
